import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function ReviewForm() {
  const { id } = useParams();
  const isNew = id === "new" || !id;
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [existing, setExisting] = useState(null);

  const [f, setF] = useState({
    client_id: sp.get("client_id") || "",
    review_type: "MBR",
    review_date: new Date().toISOString().slice(0, 10),
    period_month: new Date().toISOString().slice(0, 7),
    internal_attendees: [],
    client_attendees: [],
    issues_raised: [],
    action_items: [],
    previous_action_updates: [],
    initiatives: "",
    contract_changes: "",
    next_steps: "",
    nps_score: 8,
    nps_verbatim: "",
    csat_score: 4,
    csat_verbatim: "",
    quarterly_narrative: "",
    renewal_discussion: "",
    strategic_points: "",
    relationship_health: 4,
  });
  const [prevOpen, setPrevOpen] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/clients", { params: { page_size: 200 } });
      setClients(data.items);
      if (!isNew) {
        const r = await api.get(`/reviews/${id}`);
        setExisting(r.data);
        setLoading(false);
      }
      if (isNew && f.client_id) {
        const o = await api.get("/reviews/open-actions/list", { params: { client_id: f.client_id } });
        setPrevOpen(o.data);
      }
    })();
    /* eslint-disable-next-line */
  }, [id]);

  useEffect(() => {
    if (!isNew || !f.client_id) { setPrevOpen([]); return; }
    (async () => {
      const o = await api.get("/reviews/open-actions/list", { params: { client_id: f.client_id } });
      setPrevOpen(o.data);
    })();
  }, [f.client_id, isNew]);

  if (!isNew && loading) return <div className="p-8 text-slate-500">Loading…</div>;

  if (!isNew && existing) {
    return <ReviewView r={existing} onUpdated={async () => {
      const r = await api.get(`/reviews/${id}`); setExisting(r.data);
    }} />;
  }

  const addActionItem = () => {
    setF({ ...f, action_items: [...f.action_items, { description: "", owner_name: "", due_date: "", status: "open" }] });
  };
  const updateAction = (i, k, v) => {
    const arr = [...f.action_items]; arr[i] = { ...arr[i], [k]: v }; setF({ ...f, action_items: arr });
  };
  const removeAction = (i) => setF({ ...f, action_items: f.action_items.filter((_, idx) => idx !== i) });

  const addClientAtt = () => setF({ ...f, client_attendees: [...f.client_attendees, { name: "", designation: "" }] });
  const updateClientAtt = (i, k, v) => {
    const arr = [...f.client_attendees]; arr[i] = { ...arr[i], [k]: v }; setF({ ...f, client_attendees: arr });
  };
  const removeClientAtt = (i) => setF({ ...f, client_attendees: f.client_attendees.filter((_, idx) => idx !== i) });

  const addMcnAtt = () => setF({ ...f, internal_attendees: [...f.internal_attendees, { name: "", designation: "" }] });
  const updateMcnAtt = (i, k, v) => {
    const arr = [...f.internal_attendees]; arr[i] = { ...arr[i], [k]: v }; setF({ ...f, internal_attendees: arr });
  };
  const removeMcnAtt = (i) => setF({ ...f, internal_attendees: f.internal_attendees.filter((_, idx) => idx !== i) });

  const togglePrevUpdate = (a, status, closure_notes) => {
    const arr = [...f.previous_action_updates];
    const idx = arr.findIndex((x) => x.id === a.id);
    const entry = { id: a.id, description: a.description, owner_name: a.owner_name, status, closure_notes };
    if (idx >= 0) arr[idx] = entry; else arr.push(entry);
    setF({ ...f, previous_action_updates: arr });
  };

  const save = async () => {
    if (!f.client_id) { toast.error("Pick a client"); return; }
    setSaving(true);
    try {
      await api.post("/reviews", f);
      toast.success(`${f.review_type} recorded · email logged`);
      navigate(`/clients/${f.client_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-8 space-y-4 max-w-5xl" data-testid="review-form">
      <div>
        <div className="mptg-overline">Governance</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">New {f.review_type}</h1>
        <p className="text-slate-500 mt-1">Structured business review — minutes email auto-logged on save.</p>
      </div>

      <section className="mptg-card">
        <h3 className="font-heading font-bold">Review meta</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <div className="col-span-2"><Label>Client *</Label>
            <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
              <SelectTrigger data-testid="review-client-select"><SelectValue placeholder="Pick client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Type</Label>
            <Select value={f.review_type} onValueChange={(v) => setF({ ...f, review_type: v })}>
              <SelectTrigger data-testid="review-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="WBR">WBR (Weekly)</SelectItem>
                <SelectItem value="MBR">MBR (Monthly)</SelectItem>
                <SelectItem value="QBR">QBR (Quarterly)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Review date</Label><Input type="date" value={f.review_date} onChange={(e) => setF({ ...f, review_date: e.target.value })} /></div>
          <div><Label>Period (YYYY-MM)</Label><Input type="month" value={f.period_month} onChange={(e) => setF({ ...f, period_month: e.target.value })} /></div>
        </div>
      </section>

      <section className="mptg-card">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold">Client attendees</h3>
          <Button variant="outline" size="sm" onClick={addClientAtt} data-testid="add-client-att"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
        </div>
        <div className="space-y-2 mt-3">
          {f.client_attendees.map((a, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <Input className="col-span-5" placeholder="Name" value={a.name} onChange={(e) => updateClientAtt(i, "name", e.target.value)} />
              <Input className="col-span-6" placeholder="Designation" value={a.designation} onChange={(e) => updateClientAtt(i, "designation", e.target.value)} />
              <button onClick={() => removeClientAtt(i)} className="col-span-1 text-[#C0392B] hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 mx-auto" /></button>
            </div>
          ))}
          {f.client_attendees.length === 0 && <div className="text-xs text-slate-400">No attendees added.</div>}
        </div>
      </section>

      <section className="mptg-card">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold">MCN (internal) attendees</h3>
          <Button variant="outline" size="sm" onClick={addMcnAtt} data-testid="add-mcn-att"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
        </div>
        <div className="space-y-2 mt-3">
          {f.internal_attendees.map((a, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <Input className="col-span-5" placeholder="Name" value={a.name || ""} onChange={(e) => updateMcnAtt(i, "name", e.target.value)} data-testid={`mcn-att-name-${i}`} />
              <Input className="col-span-6" placeholder="Designation" value={a.designation || ""} onChange={(e) => updateMcnAtt(i, "designation", e.target.value)} />
              <button onClick={() => removeMcnAtt(i)} className="col-span-1 text-[#C0392B] hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 mx-auto" /></button>
            </div>
          ))}
          {f.internal_attendees.length === 0 && <div className="text-xs text-slate-400">No MCN attendees added.</div>}
        </div>
      </section>

      {prevOpen.length > 0 && (
        <section className="mptg-card">
          <h3 className="font-heading font-bold">Previous action items — update status</h3>
          <div className="space-y-2 mt-3">
            {prevOpen.map((a) => {
              const upd = f.previous_action_updates.find((x) => x.id === a.id);
              return (
                <div key={a.id} className="flex items-center gap-2">
                  <div className="flex-1 text-sm">
                    <div className="font-medium">{a.description}</div>
                    <div className="text-xs text-slate-500">{a.owner_name} · due {a.due_date || "-"}</div>
                  </div>
                  <Select value={upd?.status || a.status}
                    onValueChange={(v) => togglePrevUpdate(a, v, upd?.closure_notes || "")}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="mptg-card">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold">Action items from this review</h3>
          <Button variant="outline" size="sm" onClick={addActionItem} data-testid="add-action-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
        </div>
        <div className="space-y-2 mt-3">
          {f.action_items.map((a, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <Input className="col-span-5" placeholder="Action" value={a.description} onChange={(e) => updateAction(i, "description", e.target.value)} />
              <Input className="col-span-3" placeholder="Owner" value={a.owner_name} onChange={(e) => updateAction(i, "owner_name", e.target.value)} />
              <Input className="col-span-3" type="date" value={a.due_date || ""} onChange={(e) => updateAction(i, "due_date", e.target.value)} />
              <button onClick={() => removeAction(i)} className="col-span-1 text-[#C0392B] hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 mx-auto" /></button>
            </div>
          ))}
          {f.action_items.length === 0 && <div className="text-xs text-slate-400">No items yet.</div>}
        </div>
      </section>

      <section className="mptg-card">
        <h3 className="font-heading font-bold">Narrative</h3>
        <div className="grid grid-cols-1 gap-3 mt-3">
          <div><Label>Initiatives / escalations</Label><Textarea rows={3} value={f.initiatives} onChange={(e) => setF({ ...f, initiatives: e.target.value })} /></div>
          <div><Label>Contract / scope changes</Label><Textarea rows={2} value={f.contract_changes} onChange={(e) => setF({ ...f, contract_changes: e.target.value })} /></div>
          <div><Label>Next steps & commitments</Label><Textarea rows={3} value={f.next_steps} onChange={(e) => setF({ ...f, next_steps: e.target.value })} /></div>
        </div>
      </section>

      {f.review_type === "QBR" && (
        <section className="mptg-card">
          <h3 className="font-heading font-bold">QBR — strategic section</h3>
          <div className="grid grid-cols-1 gap-3 mt-3">
            <div><Label>Quarterly narrative</Label><Textarea rows={3} value={f.quarterly_narrative} onChange={(e) => setF({ ...f, quarterly_narrative: e.target.value })} /></div>
            <div><Label>Renewal / expansion</Label><Textarea rows={2} value={f.renewal_discussion} onChange={(e) => setF({ ...f, renewal_discussion: e.target.value })} /></div>
            <div><Label>Strategic points</Label><Textarea rows={2} value={f.strategic_points} onChange={(e) => setF({ ...f, strategic_points: e.target.value })} /></div>
            <div>
              <Label>Relationship health (1-5): <span className="font-bold">{f.relationship_health}</span></Label>
              <Slider min={1} max={5} step={1} value={[f.relationship_health]} onValueChange={([v]) => setF({ ...f, relationship_health: v })} className="mt-2" />
            </div>
          </div>
        </section>
      )}

      <section className="mptg-card">
        <h3 className="font-heading font-bold">NPS & CSAT</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
          <div>
            <Label>NPS (0-10): <span className="font-bold">{f.nps_score}</span></Label>
            <Slider min={0} max={10} step={1} value={[f.nps_score ?? 0]} onValueChange={([v]) => setF({ ...f, nps_score: v })} className="mt-2" data-testid="nps-slider" />
            <Textarea rows={2} placeholder="Verbatim…" className="mt-2" value={f.nps_verbatim} onChange={(e) => setF({ ...f, nps_verbatim: e.target.value })} />
          </div>
          <div>
            <Label>CSAT (1-5): <span className="font-bold">{f.csat_score}</span></Label>
            <Slider min={1} max={5} step={1} value={[f.csat_score ?? 1]} onValueChange={([v]) => setF({ ...f, csat_score: v })} className="mt-2" data-testid="csat-slider" />
            <Textarea rows={2} placeholder="Verbatim…" className="mt-2" value={f.csat_verbatim} onChange={(e) => setF({ ...f, csat_verbatim: e.target.value })} />
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-review-btn">
          {saving ? "Saving…" : `Save ${f.review_type} & send minutes`}
        </Button>
      </div>
    </div>
  );
}

function ReviewView({ r, onUpdated }) {
  const updateAction = async (actionId, patch) => {
    try {
      await api.post(`/reviews/${r.id}/action-items/${actionId}`, patch);
      toast.success("Action item updated");
      onUpdated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openCount = (r.action_items || []).filter((a) => a.status !== "closed").length;
  const closedCount = (r.action_items || []).filter((a) => a.status === "closed").length;
  const clientAtt = r.client_attendees || [];
  const mcnAtt = r.internal_attendees || [];

  return (
    <div className="p-6 md:p-8 space-y-4 max-w-5xl" data-testid="review-view">
      <div>
        <div className="mptg-overline">{r.review_type} · {r.period_month}</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">{r.client_name}</h1>
        <div className="text-sm text-slate-500 mt-1">
          Recorded {r.review_date} ·
          <span className="mx-2 badge-blue rounded px-1.5 py-0.5 text-[11px] font-semibold">NPS {r.nps_score ?? "-"}</span>
          <span className="badge-green rounded px-1.5 py-0.5 text-[11px] font-semibold">CSAT {r.csat_score ?? "-"}</span>
        </div>
      </div>

      {(clientAtt.length > 0 || mcnAtt.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="attendees-section">
          <div className="mptg-card">
            <h3 className="font-heading font-bold flex items-center justify-between">
              Client attendees
              <span className="text-xs text-slate-400 font-normal">{clientAtt.length}</span>
            </h3>
            <ul className="mt-3 space-y-1 text-sm">
              {clientAtt.length === 0 && <li className="text-slate-400 text-xs">Not recorded.</li>}
              {clientAtt.map((a, i) => (
                <li key={i} className="flex items-center justify-between border-b border-slate-100 pb-1 last:border-0">
                  <span className="font-medium">{a.name || "-"}</span>
                  <span className="text-xs text-slate-500">{a.designation || ""}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mptg-card">
            <h3 className="font-heading font-bold flex items-center justify-between">
              MCN attendees
              <span className="text-xs text-slate-400 font-normal">{mcnAtt.length}</span>
            </h3>
            <ul className="mt-3 space-y-1 text-sm">
              {mcnAtt.length === 0 && <li className="text-slate-400 text-xs">Not recorded.</li>}
              {mcnAtt.map((a, i) => (
                <li key={i} className="flex items-center justify-between border-b border-slate-100 pb-1 last:border-0">
                  <span className="font-medium">
                    {typeof a === "string" ? a : (a?.name || "-")}
                  </span>
                  <span className="text-xs text-slate-500">
                    {typeof a === "string" ? "" : (a?.designation || "")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {(r.action_items || []).length > 0 && (
        <div className="mptg-card" data-testid="actions-section">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold">Action items · discussion points</h3>
            <div className="flex items-center gap-2 text-xs">
              <span className="badge-amber rounded px-2 py-0.5 font-semibold">{openCount} open</span>
              <span className="badge-green rounded px-2 py-0.5 font-semibold">{closedCount} closed</span>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {r.action_items.map((a) => <ActionItemRow key={a.id} a={a} onUpdate={(patch) => updateAction(a.id, patch)} />)}
          </div>
        </div>
      )}

      {r.next_steps && (
        <div className="mptg-card">
          <h3 className="font-heading font-bold">Next steps</h3>
          <p className="mt-2 text-sm whitespace-pre-wrap">{r.next_steps}</p>
        </div>
      )}
      {r.initiatives && (
        <div className="mptg-card">
          <h3 className="font-heading font-bold">Initiatives</h3>
          <p className="mt-2 text-sm whitespace-pre-wrap">{r.initiatives}</p>
        </div>
      )}
      {r.contract_changes && (
        <div className="mptg-card">
          <h3 className="font-heading font-bold">Contract / scope changes</h3>
          <p className="mt-2 text-sm whitespace-pre-wrap">{r.contract_changes}</p>
        </div>
      )}
      {r.nps_verbatim && (
        <div className="mptg-card">
          <h3 className="font-heading font-bold">NPS verbatim</h3>
          <p className="mt-2 text-sm whitespace-pre-wrap text-slate-700">"{r.nps_verbatim}"</p>
        </div>
      )}
    </div>
  );
}

function ActionItemRow({ a, onUpdate }) {
  const [status, setStatus] = useState(a.status);
  const [notes, setNotes] = useState(a.closure_notes || "");
  const [editingNotes, setEditingNotes] = useState(false);

  useEffect(() => { setStatus(a.status); setNotes(a.closure_notes || ""); }, [a.status, a.closure_notes]);

  const onStatusChange = (v) => {
    setStatus(v);
    onUpdate({ status: v, closure_notes: notes });
    if (v === "closed") setEditingNotes(true);
  };

  const saveNotes = () => {
    onUpdate({ status, closure_notes: notes });
    setEditingNotes(false);
  };

  const statusCls = status === "closed" ? "badge-green" : status === "in_progress" ? "badge-amber" : "badge-red";

  return (
    <div className="border border-slate-200 rounded p-3" data-testid={`action-item-${a.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{a.description}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Owner: <span className="text-slate-700 font-medium">{a.owner_name}</span> · Due: {a.due_date || "-"}
          </div>
          {a.closure_notes && !editingNotes && (
            <div className="mt-2 text-xs text-slate-700 italic bg-[#F8FAFC] rounded p-2 border border-slate-100">
              <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px] mr-2">Closure</span>
              {a.closure_notes}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`rounded px-2 py-0.5 text-[11px] font-semibold uppercase ${statusCls}`}>{status.replace("_", " ")}</span>
          <Select value={status} onValueChange={onStatusChange}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid={`action-status-${a.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {editingNotes && (
        <div className="mt-3 flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs text-slate-500">Closure notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Outcome, evidence, follow-ups…" className="mt-1" data-testid={`closure-notes-${a.id}`} />
          </div>
          <Button size="sm" onClick={saveNotes} className="bg-[#1E8449] hover:bg-[#1a6e3e] text-white" data-testid={`save-notes-${a.id}`}>
            Save notes
          </Button>
        </div>
      )}
      {!editingNotes && status !== "open" && (
        <button onClick={() => setEditingNotes(true)} className="text-xs text-[#2E86C1] hover:underline mt-2"
          data-testid={`edit-notes-${a.id}`}>
          {a.closure_notes ? "Edit closure notes" : "+ Add closure notes"}
        </button>
      )}
    </div>
  );
}
